'use client'

import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import { ProductCardHoverProvider } from 'product-card/context/ProductCardHoverContext'
import { ProductCardRefProvider } from 'product-card/context/ProductCardRefContext'
import { useProductCardContext } from 'product-card/hooks/useProductCardContext'
import { useProductInfo } from 'product/services/productInfo/client'
import type { Product, ProductImageType } from 'product/types'
import type { ProductPrices } from 'product/types/Prices'
import { useMemo, useRef } from 'react'
import { useMatchMedia } from 'utils/matchMedia/hooks'

import { ProductCardActionButtons } from './ProductCardActionButtons'
import { ProductDetails } from './ProductDetails'
import { ProductImageContainer } from './ProductImageContainer'

import styles from './ProductCard.module.scss'
import mq from 'utils/matchMedia/styles/MatchMediaQueries.module.scss'

interface ProductCardBaseProps {
	product: Product | null
	isLoading?: boolean
	imageSortCriteria: ProductImageType[]
	prices?: ProductPrices // This property allows us to manage the call for product pricing from the client or server as needed.
	priority?: boolean
	isTwoColumnSize?: boolean
	className?: string
}

export const ProductCard = ({
	product,
	isLoading = false,
	imageSortCriteria,
	priority,
	prices,
	isTwoColumnSize,
	className,
}: ProductCardBaseProps) => {
	const productCardRef = useRef<HTMLDivElement | null>(null)
	const [isLargeOrGreater] = useMatchMedia([mq.islargeorgreater])
	/**
	 * This product has been marked to span two columns, this means that is a featured product on large screen devices.
	 */
	const showAsFeatured = !!(isTwoColumnSize && isLargeOrGreater)

	const {
		country: { countryISO, languageISO },
	} = useMasterData()

	const { productId, shouldFetchProduct, dataTestId } = useProductCardContext()

	const { data: productData } = useProductInfo({
		productId,
		country: countryISO,
		language: languageISO,
		enabled: shouldFetchProduct,
	})

	const currentProduct = useMemo(
		() => productData ?? product,
		[productData, product]
	)

	const isProductCardReadyForRender = !isLoading && currentProduct

	if (isProductCardReadyForRender) {
		return (
			<ProductCardRefProvider initialProductCardRef={productCardRef}>
				<div
					ref={productCardRef}
					className={`${styles.productCard} ${className}`}
					data-testid={dataTestId}
				>
					<div>
						<ProductCardHoverProvider>
							<ProductImageContainer
								productId={productId}
								product={currentProduct}
								imageSortCriteria={imageSortCriteria}
								priority={priority}
								showAsFeatured={showAsFeatured}
							/>
						</ProductCardHoverProvider>
						<ProductDetails
							productId={productId}
							product={currentProduct}
							prices={prices}
							showAsFeatured={showAsFeatured}
						/>
					</div>
					<ProductCardActionButtons productColors={currentProduct.colors} />
				</div>
			</ProductCardRefProvider>
		)
	}

	return null
}
